import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';
import Flag from 'react-flags';
import countryList from 'country-list';

import styles from './CountrySwitch.module.css';
import { useSelector } from 'react-redux';

function CountrySwitch({ transparent }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useSelector(state => state.user.user);

    // Remove default selected country state - let it be null initially
    const [selectedCountry, setSelectedCountry] = useState(null);
    const countries = useMemo(() => countryList.getData(), []);
    
    // Check if current page is Jobs or Jobseekers
    const isJobsPage = location.pathname.includes('/jobs');
    const isJobseekersPage = location.pathname.includes('/jobseekers');
    
    // Generate country options from country-list
    const options = useMemo(() => {
        // Common GCC countries to show at the top
        const gccCountryCodes = ['QA', 'AE', 'SA', 'BH', 'KW', 'OM'];

        // Create options for GCC countries
        const gccOptions = countries
            .filter(country => gccCountryCodes.includes(country.code))
            .map(country => ({
                label: t(`general.country.${country.code}`) || country.name,
                value: country.code,
                flag: country.code
            }));

        // Create options for all other countries
        const otherOptions = countries
            .filter(country => !gccCountryCodes.includes(country.code))
            .map(country => ({
                label: t(`general.country.${country.code}`) || country.name,
                value: country.code,
                flag: country.code
            }));

        // Return appropriate options based on the current page
        if (isJobsPage && !isJobseekersPage) {
            // Add GCC option at the top for Jobs page
            const gccOption = {
                label: 'GCC',
                value: 'GCC',
                flag: 'QA', // Using Qatar flag as representative for GCC
                isDefault: true
            };
            
            // Only GCC countries for Jobs page with GCC option at top
            return [gccOption, ...gccOptions]; 
        } else {
            // Add World option at the top for all other pages
            const worldOption = {
                label: 'World',
                value: 'world',
                isDefault: true
            };
            
            // For all other pages, include all countries with World option at top
            return [worldOption, ...gccOptions, ...otherOptions];
        }
    }, [countries, t, isJobsPage, isJobseekersPage]);

    // Get placeholder based on user account type
    const getPlaceholder = () => {
        if (user?.account_type === 'employer') {
            return 'GCC';
        } else {
            return 'World';
        }
    };

    // Extract current country filter from URL if on jobseekers page
    useEffect(() => {
        // Get country from localStorage first (for persistence across page loads)
        const savedCountry = localStorage.getItem('selectedCountry');
        if (savedCountry) {
            try {
                const parsedCountry = JSON.parse(savedCountry);
                const country = options.find(opt => opt.value === parsedCountry.value);
                if (country) {
                    setSelectedCountry(country);
                    return;
                }
            } catch (e) {
                console.error('Error parsing saved country', e);
            }
        }

        // If no saved country, try to get from URL
        const query = new URLSearchParams(location.search);
        const countryParam = query.get('country');

        if (countryParam) {
            const country = options.find(opt => opt.value === countryParam);
            if (country) {
                setSelectedCountry(country);
                localStorage.setItem('selectedCountry', JSON.stringify(country));
            }
        } else {
            // If no country in URL and no saved country, set to default option based on page
            const defaultOption = options.find(opt => opt.isDefault);
            if (defaultOption) {
                setSelectedCountry(defaultOption);
            } else {
                setSelectedCountry(null);
            }
        }
    }, [location.search, options]);

    // Add a new useEffect to listen for localStorage changes
    useEffect(() => {
        const handleStorageChange = () => {
            const savedCountry = localStorage.getItem('selectedCountry');
            if (savedCountry) {
                try {
                    const parsedCountry = JSON.parse(savedCountry);
                    const country = options.find(opt => opt.value === parsedCountry.value);
                    if (country) {
                        setSelectedCountry(country);
                    }
                } catch (e) {
                    console.error('Error parsing saved country', e);
                    // If error parsing, set to default option based on page
                    const defaultOption = options.find(opt => opt.isDefault);
                    if (defaultOption) {
                        setSelectedCountry(defaultOption);
                    } else {
                        setSelectedCountry(null);
                    }
                }
            } else {
                // If no country in localStorage, set to default option based on page
                const defaultOption = options.find(opt => opt.isDefault);
                if (defaultOption) {
                    setSelectedCountry(defaultOption);
                } else {
                    setSelectedCountry(null);
                }
            }
        };

        // Listen for storage events (when localStorage changes in other tabs/windows)
        window.addEventListener('storage', handleStorageChange);
        
        // Also check localStorage on mount and when options change
        handleStorageChange();

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [options]);

    const Input = ({ ...rest }) => <components.Input {...rest} autoComplete={'do-not-autofill'} />;

    const customSingleValue = ({ data }) => {
        // Special handling for World option
        if (data.value === 'world') {
            return (
                <div className="country-select">
                    <span style={{  fontSize: '16px' }}>🌎</span>
                    <span>{data.label}</span>
                </div>
            );
        }
        
        return (
            <div className="country-select">
                <Flag
                    name={data.flag}
                    format="svg"
                    shiny={false}
                    basePath="/vendor/flags"
                    className={styles.flagIcon}
                />
                <span>{data.value}</span>
                {/* <span>{data.label}</span> */}
            </div>
        );
    };

    const Option = props => {
        // Special handling for World option
        if (props.data.value === 'world') {
            return (
                <components.Option {...props}>
                    <div className={styles.countryOption}>
                        <span style={{  fontSize: '16px' }}>🌎</span>
                        <span>{props.data.label}</span>
                    </div>
                </components.Option>
            );
        }

        return (
            <components.Option {...props}>
                <div className={styles.countryOption}>
                    <Flag
                        name={props.data.flag}
                        format="svg"
                        shiny={false}
                        basePath="/vendor/flags"
                        className={styles.flagIcon}
                    />
                    <span>{props.data.label}</span>
                </div>
            </components.Option>
        );
    };

    const DropdownIndicator = props => {
        return transparent ? null : <components.DropdownIndicator {...props} />;
    };

    const handleChange = (selected) => {
        if (!selected) return;

        // Update the selected country state first
        setSelectedCountry(selected);

        // If selecting the default options (World or GCC), clear any country filter
        if (selected.value === 'world' || selected.value === 'GCC') {
            localStorage.removeItem('selectedCountry');
            
            // Redirect to the page without country filter
            if (location.pathname.includes('/jobseekers')) {
                const query = new URLSearchParams(location.search);
                query.delete('country');
                query.set('page', '1');
                window.location.href = `${location.pathname}?${query.toString()}`;
                return;
            } else if (location.pathname.includes('/jobs') && !location.pathname.includes('/employer-portal/jobs')) {
                window.location.href = 'jobs?page=1&sortby=relevance&exclusiveJobs=false&minimum_salary=1&maximum_salary=9000';
                return;
            }
        }

        // Save to localStorage for persistence across page loads
        localStorage.setItem('selectedCountry', JSON.stringify(selected));

        let targetUrl = '';

        // Apply filter based on current page
        if (location.pathname.includes('/jobseekers')) {
            // For jobseekers search page
            const query = new URLSearchParams(location.search);
            query.set('country', selected.value);
            query.set('page', '1'); // Reset to first page
            targetUrl = `${location.pathname}?${query.toString()}`;
            window.location.href = targetUrl;
        } else if (location.pathname.includes('/jobs') && !location.pathname.includes('/employer-portal/jobs')) {
            if(selected.value === 'GCC'){
                targetUrl = `jobs?page=1&sortby=relevance&exclusiveJobs=false&minimum_salary=1&maximum_salary=9000`;
            }else{
                targetUrl = `jobs?page=1&sortby=relevance&country=${selected.value}&exclusiveJobs=false&minimum_salary=1&maximum_salary=9000`;
            }
            window.location.href = targetUrl;
        } else {
            targetUrl = `/jobseekers?country=${selected.value}&page=1`;
        }
    };

    const customStyles = transparent ? {
        control: (provided) => ({
            ...provided,
            backgroundColor: 'white',
            boxShadow: 'none',
            border: 'none',
            minHeight: '36px',
            height: '36px'
        }),
        indicatorSeparator: () => ({
            display: 'none'
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: '36px',
            padding: '0 8px'
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999
        }),
        menuList: (provided) => ({
            ...provided,
            maxHeight: '200px'
        }),
        option: (provided) => ({
            ...provided,
            padding: '8px 12px'
        })
    } : {
        menu: (provided) => ({
            ...provided,
            zIndex: 9999
        }),
        menuList: (provided) => ({
            ...provided,
            maxHeight: '200px'
        })
    };

    return (
        <Select
            className={`countrySwitch ${styles.countrySwitch}`}
            classNamePrefix="react-country-select"
            options={options}
            styles={customStyles}
            value={selectedCountry}
            placeholder={getPlaceholder()}
            components={{
                SingleValue: customSingleValue,
                Input: Input,
                DropdownIndicator: DropdownIndicator,
                Option: Option
            }}
            onChange={handleChange}
            isSearchable={true}
        />
    );
}

export default CountrySwitch; 